/**
 * V7Vista CRM Integration — Nausiddhi Realty
 * API docs: https://v7vista.tranquilcrmone.in/apidoc/3
 */

const CRM_ENDPOINT = 'https://v7vista.tranquilcrmone.in/v2/createlead';
const API_KEY      = 'TRNQUILCRMnausiddhi';
const SOURCE_TYPE  = '3';

/**
 * CRM project registry.
 * `slug` matches the React Router param used in ProjectDetailPage
 * (i.e. the id in projectsStore). `aliases` cover legacy/alternate
 * spellings so older links and admin-typed names still resolve.
 *
 * NOTE: Dapoli (Seaview Enclaves) has no CRM project id yet —
 * it resolves to 4 ("Not decided yet") until the client provisions one
 * in V7Vista. Update `id` here when they do.
 */
export const CRM_PROJECTS = [
  { id: 4, name: 'Not decided yet',       slug: 'any',
    aliases: ['nausiddhi-coastal-greens', 'coastal-greens', 'dapoli', 'seaview-enclaves', 'seaview', 'seaview enclaves', 'seaview-enclave', 'seacleave-enclaves', 'seacleave', 'seacleave enclaves'] },
  { id: 1, name: 'BlueRain Resort Homes', slug: 'blue-rain-resort-homes',
    aliases: ['bluerain-resort-homes', 'blue rain resort homes'] },
  { id: 2, name: 'Sai Srushti',           slug: 'sai-shrusti',
    aliases: ['sai-srushti', 'sai shrusti'] },
  { id: 3, name: 'Nausiddhi Residence',   slug: 'nausiddhi-residency',
    aliases: ['nausiddhi-residence', 'nausiddhi residency'] },
];

/**
 * Resolve a route slug, project name, or raw numeric string → CRM project ID.
 * Falls back to 4 ("Any Project") when no match is found.
 */
export function resolveCRMProjectId(slugOrName) {
  if (!slugOrName) return 4;
  const s = String(slugOrName).toLowerCase().trim();
  const match = CRM_PROJECTS.find(
    (p) =>
      p.slug === s ||
      p.name.toLowerCase() === s ||
      String(p.id) === s ||
      (p.aliases && p.aliases.includes(s))
  );
  return match ? match.id : 4;
}

/** Pull UTM / gclid params from the current URL. */
function getUTMParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      campaign_name: p.get('utm_campaign') || undefined,
      adgroup_name:  p.get('utm_medium')   || undefined,
      ad_name:       p.get('utm_content')  || undefined,
      gcl_id:        p.get('gclid')        || undefined,
      sub_source:    p.get('utm_source')   || 'WebForm',
    };
  } catch {
    return { sub_source: 'WebForm' };
  }
}

/**
 * Submit a lead to the CRM.
 *
 * @param {Object}  lead
 * @param {string}  lead.phone       – 10-digit mobile (no country code)
 * @param {number}  lead.projectId   – CRM project ID 1–4
 * @param {string} [lead.name]
 * @param {string} [lead.email]
 * @param {string} [lead.remark]
 * @param {string} [lead.minBudget]  – e.g. "20L"
 * @param {string} [lead.budget]     – e.g. "80L"
 * @param {string} [lead.leadType]   – "rental" | "resale" | "sales"
 *
 * @returns {Promise<{ status: boolean, msg: string }>}
 */
export async function submitLead(lead) {
  const utm = getUTMParams();

  const params = new URLSearchParams();
  params.set('api_key',         API_KEY);
  params.set('country_code',    '91');
  params.set('mobile_number',   lead.phone.replace(/\D/g, ''));
  params.set('project_id',      String(lead.projectId ?? 4));
  params.set('project_id_type', 'id');
  params.set('source_type',     SOURCE_TYPE);
  params.set('sub_source',      utm.sub_source ?? 'WebForm');

  if (lead.name)             params.set('customer_name',  lead.name.trim());
  if (lead.email)            params.set('email',          lead.email.trim());
  if (lead.remark)           params.set('remark',         lead.remark.trim());
  if (lead.minBudget)        params.set('min_budget',     lead.minBudget);
  if (lead.budget)           params.set('budget',         lead.budget);
  if (lead.leadType)         params.set('lead_type',      lead.leadType);
  if (utm.campaign_name)     params.set('campaign_name',  utm.campaign_name);
  if (utm.adgroup_name)      params.set('adgroup_name',   utm.adgroup_name);
  if (utm.ad_name)           params.set('ad_name',        utm.ad_name);
  if (utm.gcl_id)            params.set('gcl_id',         utm.gcl_id);

  const response = await fetch(`${CRM_ENDPOINT}?${params.toString()}`);
  if (!response.ok) throw new Error(`CRM HTTP ${response.status}`);

  const data = await response.json();
  // Both "Lead punched" and "Duplicate" are treated as success — the lead exists.
  return data;
}
